// src/client/renderer/vfx/items/sprite-textures.ts
//
// Shared alpha masks for item VFX sprites. A THREE.SpriteMaterial without a
// texture renders as a rectangular billboard, which is visually obvious on the
// board. These masks keep sprite-based effects soft-edged and non-square while
// preserving each material's existing color, opacity, and blending settings.

import * as THREE from 'three';

let softDiscTexture: THREE.DataTexture | null = null;
let smokeTexture: THREE.DataTexture | null = null;

export const ITEM_VFX_SMOKE_ALPHA_TEST = 0.045;

function createSoftDiscTexture(): THREE.DataTexture {
  const size = 64;
  const data = new Uint8Array(size * size * 4);
  const center = (size - 1) / 2;

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const dx = (x - center) / center;
      const dy = (y - center) / center;
      const distance = Math.sqrt(dx * dx + dy * dy);
      const falloff = THREE.MathUtils.clamp(1 - distance, 0, 1);
      const alpha = Math.round(Math.pow(falloff, 1.8) * 255);
      const i = (y * size + x) * 4;
      data[i] = 255;
      data[i + 1] = 255;
      data[i + 2] = 255;
      data[i + 3] = alpha;
    }
  }

  const texture = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
  texture.name = 'item-vfx-soft-disc';
  texture.needsUpdate = true;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  return texture;
}

export function getItemVfxSoftDiscTexture(): THREE.Texture {
  softDiscTexture ??= createSoftDiscTexture();
  return softDiscTexture;
}

function cloudNoise(x: number, y: number): number {
  const value = Math.sin(x * 12.9898 + y * 78.233) * 43758.5453;
  return value - Math.floor(value);
}

function createSmokeTexture(): THREE.DataTexture {
  const size = 96;
  const data = new Uint8Array(size * size * 4);
  const center = (size - 1) / 2;

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const nx = (x - center) / center;
      const ny = (y - center) / center;
      const distance = Math.sqrt(nx * nx + ny * ny);
      const radial = THREE.MathUtils.smoothstep(1 - distance, 0, 1);
      const edgeFade = THREE.MathUtils.smoothstep(1 - distance, 0.14, 0.42);
      const lobeA = THREE.MathUtils.clamp(1 - Math.hypot(nx + 0.18, ny - 0.12) / 0.78, 0, 1);
      const lobeB = THREE.MathUtils.clamp(1 - Math.hypot(nx - 0.2, ny + 0.08) / 0.7, 0, 1);
      const lobeC = THREE.MathUtils.clamp(1 - Math.hypot(nx, ny + 0.24) / 0.6, 0, 1);
      const lobe = Math.max(radial * 0.7, lobeA * 0.8, lobeB * 0.65, lobeC * 0.55);
      const coarse = cloudNoise(Math.floor(x / 6), Math.floor(y / 6));
      const fine = cloudNoise(x, y);
      const alpha =
        edgeFade <= 0.001
          ? 0
          : Math.round(
              THREE.MathUtils.clamp(
                (lobe * (0.62 + coarse * 0.26) + fine * 0.08) * edgeFade,
                0,
                1,
              ) * 205,
            );
      const i = (y * size + x) * 4;
      data[i] = 255;
      data[i + 1] = 255;
      data[i + 2] = 255;
      data[i + 3] = alpha;
    }
  }

  const texture = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
  texture.name = 'item-vfx-smoke-alpha';
  texture.needsUpdate = true;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  return texture;
}

export function getItemVfxSmokeTexture(): THREE.Texture {
  smokeTexture ??= createSmokeTexture();
  return smokeTexture;
}

export function applyItemVfxSpriteMasks(root: THREE.Object3D): void {
  const texture = getItemVfxSoftDiscTexture();
  root.traverse((child) => {
    if (!(child instanceof THREE.Sprite)) return;
    const materials = Array.isArray(child.material) ? child.material : [child.material];
    for (const material of materials) {
      if (!(material instanceof THREE.SpriteMaterial)) continue;
      if (material.map == null) {
        material.map = texture;
        material.alphaTest = Math.max(material.alphaTest, 0.01);
        material.needsUpdate = true;
      }
    }
  });
}
